import { useCallback, useEffect, useState } from "react";
import { useTranslationContext } from "../../../context";
import { ApiError } from "../../../utilities/apiClient";
import type { ProvetApiClient } from "../../../utilities/provetApiUtilities";

interface UsePhoneVerificationReturn {
  sendVerificationCode: (phoneNumber: string, dispatchId?: string) => Promise<void>;
  verifyCode: (
    email: string,
    phoneNumber: string,
    code: string,
    sessionId: string
  ) => Promise<boolean>;
  /** Sends the code again. Resolves to whether a code was sent (not during the cooldown). */
  resendCode: () => Promise<boolean>;
  /**
   * Forgets the code that was sent, eg when the user goes back to change the phone number.
   * The resend cooldown keeps running, so that going back doesn't skip it.
   */
  resetVerification: () => void;
  isLoading: boolean;
  error: Error | null;
  requestId: string | null;
  canResend: boolean;
  resendCooldown: number;
}

interface UsePhoneVerificationOptions {
  api: ProvetApiClient;
  resendDelaySeconds?: number;
}

/**
 * Hook for managing phone verification with SMS Verification API
 * Uses ProvetApi client for consistent CSRF token handling and error management
 */
export const usePhoneVerification = ({
  api,
  resendDelaySeconds = 10
}: UsePhoneVerificationOptions): UsePhoneVerificationReturn => {
  const translationContext = useTranslationContext();
  const translations = translationContext?.translations;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [lastPhoneNumber, setLastPhoneNumber] = useState<string | null>(null);
  const [lastSentTime, setLastSentTime] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const sendVerificationCode = useCallback(
    async (phoneNumber: string, dispatchId?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.createVerification(phoneNumber, dispatchId);
        setRequestId(response.requestId);
        setLastPhoneNumber(phoneNumber);
        setLastSentTime(Date.now());
      } catch (err) {
        let error: Error;
        if (err instanceof ApiError && err.statusCode !== undefined) {
          // The server answers every failure the same way, whether it was a rate limit,
          // Twilio refusing or a fault, so the customer gets one message they can act on.
          // Network errors and timeouts (no status) keep their own readable message.
          error = new Error(
            translations?.errorSendCodeUnavailable ||
              "We couldn't send a code right now. Please wait a few minutes and try again."
          );
        } else if (err instanceof Error) {
          error = err;
        } else {
          error = new Error(
            translations?.errorSendCodeFailed || "Failed to send code. Please try again."
          );
        }
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [api]
  );

  const verifyCode = useCallback(
    async (
      email: string,
      phoneNumber: string,
      code: string,
      sessionId: string
    ): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        if (!requestId) {
          const error = new Error(
            translations?.errorNoRequestId || "No request ID available for verification"
          );
          setError(error);
          return false;
        }

        const response = await api.checkVerification(
          email,
          phoneNumber,
          code,
          sessionId,
          requestId
        );

        if (!response.valid) {
          const error = new Error(
            translations?.errorInvalidCode || "Invalid code. Please try again."
          );
          setError(error);
          return false;
        }

        return true;
      } catch (err) {
        // A check the server turned away (rate limit, Twilio refusing, a fault) looks exactly like
        // a wrong code, so the customer's message doesn't reveal the limit. Network errors and
        // timeouts (no status) keep their own readable message.
        const error =
          err instanceof ApiError && err.statusCode !== undefined
            ? new Error(
                translations?.errorInvalidCode || "Invalid code. Please try again."
              )
            : err instanceof Error
              ? err
              : new Error(
                  translations?.errorVerifyCodeFailed ||
                    "Failed to verify code. Please try again."
                );
        setError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [api, requestId]
  );

  const resendCode = useCallback(async (): Promise<boolean> => {
    if (!lastPhoneNumber) {
      const error = new Error(
        translations?.errorNoPhoneNumber || "No phone number available for resend"
      );
      setError(error);
      return false;
    }

    // Check if enough time has passed
    if (lastSentTime) {
      const timeSinceLastSend = (Date.now() - lastSentTime) / 1000;
      if (timeSinceLastSend < resendDelaySeconds) {
        return false; // Still in cooldown, do nothing
      }
    }

    await sendVerificationCode(lastPhoneNumber);
    return true;
  }, [
    lastPhoneNumber,
    sendVerificationCode,
    translations,
    lastSentTime,
    resendDelaySeconds
  ]);

  const resetVerification = useCallback(() => {
    setError(null);
    setRequestId(null);
    setLastPhoneNumber(null);
  }, []);

  // Update cooldown timer
  useEffect(() => {
    if (!lastSentTime) {
      setResendCooldown(0);
      return;
    }

    const updateCooldown = () => {
      const timeSinceLastSend = (Date.now() - lastSentTime) / 1000;
      const remaining = Math.max(0, Math.ceil(resendDelaySeconds - timeSinceLastSend));
      setResendCooldown(remaining);

      if (remaining > 0) {
        return true; // Continue interval
      }
      return false; // Stop interval
    };

    // Initial update
    if (!updateCooldown()) {
      return;
    }

    // Update every second
    const intervalId = setInterval(() => {
      if (!updateCooldown()) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [lastSentTime, resendDelaySeconds]);

  const canResend = resendCooldown === 0;

  return {
    sendVerificationCode,
    verifyCode,
    resendCode,
    resetVerification,
    isLoading,
    error,
    requestId,
    canResend,
    resendCooldown
  };
};

export default usePhoneVerification;
